/**
 * Device Detection Utilities
 * Comprehensive mobile/tablet/desktop detection for Helix 2000 MMORPG
 */

export enum DeviceType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
  TABLET = 'tablet',
}

export enum OperatingSystem {
  IOS = 'ios',
  ANDROID = 'android',
  WINDOWS = 'windows',
  MACOS = 'macos',
  LINUX = 'linux',
  UNKNOWN = 'unknown',
}

export interface DeviceInfo {
  type: DeviceType;
  os: OperatingSystem;
  isTouchDevice: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  pixelRatio: number;
  isRetina: boolean;
}

// Breakpoints (matching your existing CSS)
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
  WIDE: 1440,
} as const;

/**
 * Detect if the current device is mobile
 */
export const isMobileDevice = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA =
    /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isSmallScreen = window.innerWidth < BREAKPOINTS.TABLET;
  return isMobileUA || isSmallScreen;
};

/**
 * Detect if the current device is a tablet
 */
export const isTabletDevice = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  const isTabletUA = /ipad|android(?!.*mobile)|tablet/i.test(ua);
  const isTabletScreen =
    window.innerWidth >= BREAKPOINTS.TABLET &&
    window.innerWidth < BREAKPOINTS.DESKTOP;
  return isTabletUA || (isTabletScreen && hasTouchSupport());
};

/**
 * Detect if the device has touch support
 */
export const hasTouchSupport = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  );
};

/**
 * Get the device type
 */
export const getDeviceType = (): DeviceType => {
  if (isMobileDevice()) return DeviceType.MOBILE;
  if (isTabletDevice()) return DeviceType.TABLET;
  return DeviceType.DESKTOP;
};

/**
 * Detect the operating system
 */
export const getOperatingSystem = (): OperatingSystem => {
  const ua = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) return OperatingSystem.IOS;
  if (/android/.test(ua)) return OperatingSystem.ANDROID;
  if (/win/.test(ua)) return OperatingSystem.WINDOWS;
  if (/mac/.test(ua)) return OperatingSystem.MACOS;
  if (/linux/.test(ua)) return OperatingSystem.LINUX;

  return OperatingSystem.UNKNOWN;
};

/**
 * Get screen orientation
 */
export const getOrientation = (): 'portrait' | 'landscape' => {
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
};

/**
 * Check if device is in landscape mode
 */
export const isLandscape = (): boolean => {
  return getOrientation() === 'landscape';
};

/**
 * Check if device is in portrait mode
 */
export const isPortrait = (): boolean => {
  return getOrientation() === 'portrait';
};

/**
 * Check if device has retina display
 */
export const isRetinaDisplay = (): boolean => {
  return window.devicePixelRatio > 1;
};

/**
 * Get comprehensive device information
 */
export const getDeviceInfo = (): DeviceInfo => {
  return {
    type: getDeviceType(),
    os: getOperatingSystem(),
    isTouchDevice: hasTouchSupport(),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    orientation: getOrientation(),
    pixelRatio: window.devicePixelRatio || 1,
    isRetina: isRetinaDisplay(),
  };
};

/**
 * Check if device meets minimum requirements for mobile gaming
 */
export const meetsMinimumRequirements = (): boolean => {
  const info = getDeviceInfo();

  // Minimum screen size (landscape)
  if (info.orientation === 'landscape' && info.screenWidth < 568) {
    return false;
  }

  // Minimum screen size (portrait)
  if (info.orientation === 'portrait' && info.screenWidth < 320) {
    return false;
  }

  return true;
};

/**
 * Get recommended graphics quality based on device
 */
export const getRecommendedQuality = (): 'low' | 'medium' | 'high' => {
  const info = getDeviceInfo();

  if (info.type === DeviceType.DESKTOP) return 'high';

  // Check for high-end mobile devices
  if (info.pixelRatio >= 3 && info.screenWidth >= 375) {
    return 'medium';
  }

  return 'low';
};

/**
 * Log device information to console (for debugging)
 */
export const logDeviceInfo = (): void => {
  const info = getDeviceInfo();
  console.log('🔍 [Device Detection]', {
    ...info,
    userAgent: navigator.userAgent,
    minRequirements: meetsMinimumRequirements(),
    recommendedQuality: getRecommendedQuality(),
  });
};
